import { cn } from '@/lib/utils';

import {
  CORE_INPUT_CLASS,
  CORE_INPUT_CLASS_MUTED_DISABLED,
  CORE_INPUT_INVALID_CLASS,
  CORE_INPUT_SUCCESS_CLASS,
} from './coreRendererClasses';

/**
 * CoreInput — wspólny bare-input dla rendererów kartotek (`*CoreRenderers.tsx`).
 *
 * Zastępuje 9 niemal identycznych klonów lokalnego `TextInput` (audyt P2).
 * Kontrakt renderera: `onChange(v: string)` (nie natywne zdarzenie), bez własnej
 * etykiety/hintu — label rysuje `<Field>` w rendererze. To NIE jest kit-owy
 * `Input` (ten ma label/hint/error i `onChange(e)`); CoreInput jest niżej.
 *
 * Superset zachowuje różnice, które w klonach były nośne:
 *  - `onBlur`      — lookup NIP (kontrahent, MF Biała Lista),
 *  - `invalid`     — `aria-invalid` + czerwona ramka (faktury),
 *  - `success`     — zielona ramka (walidacja potwierdzona),
 *  - `readOnly`    — pola tylko-do-odczytu (umowy),
 *  - `step`/`min`/`max` — pola liczbowe z zakresem (faktury, umowy, stawki),
 *  - `variant`     — 'mutedDisabled' wyszarza tekst pola wyłączonego (dokumenty,
 *                    umowy, faktury); 'base' dla reszty. Piksel-parytet z klonami.
 */
export interface CoreInputProps {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  invalid?: boolean;
  success?: boolean;
  type?: string;
  testId?: string;
  maxLength?: number;
  step?: string;
  min?: string;
  max?: string;
  placeholder?: string;
  variant?: 'base' | 'mutedDisabled';
  className?: string;
}

export function CoreInput({
  value,
  onChange,
  onBlur,
  disabled,
  readOnly,
  invalid,
  success,
  type = 'text',
  testId,
  maxLength,
  step,
  min,
  max,
  placeholder,
  variant = 'base',
  className,
}: CoreInputProps) {
  const base = variant === 'mutedDisabled' ? CORE_INPUT_CLASS_MUTED_DISABLED : CORE_INPUT_CLASS;
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      readOnly={readOnly}
      maxLength={maxLength}
      step={step}
      min={min}
      max={max}
      placeholder={placeholder}
      data-testid={testId}
      aria-invalid={invalid || undefined}
      className={cn(
        base,
        invalid && CORE_INPUT_INVALID_CLASS,
        !invalid && success && CORE_INPUT_SUCCESS_CLASS,
        className,
      )}
    />
  );
}
